import type { ReactNode } from "react";
import type { TravelPartyMember } from "../../types";

interface WhoWasThereProps {
  /** The trip's travel party — the chips, in party order. */
  travelParty: Record<string, TravelPartyMember>;
  /** Member keys currently shown as present. */
  selected: string[];
  onToggle: (memberId: string) => void;
  label?: string;
  /** Autosave indicator, for the always-on editors. */
  status?: "idle" | "saving" | "saved";
  /** Right-hand header slot — e.g. a "reset to inherited" button. */
  action?: ReactNode;
  /** One line under the chips explaining where an unset group comes from. */
  hint?: ReactNode;
}

/**
 * "Who was here" toggle chips over the trip's travel party. Shared by the
 * activity detail page and the segment form so a group is picked the same way
 * wherever it's set; `selected` is the *resolved* group (see
 * `utils/participants`), so an inherited default renders exactly like an
 * explicit one until someone taps a chip.
 */
export function WhoWasThere({
  travelParty,
  selected,
  onToggle,
  label = "Who was here",
  status,
  action,
  hint,
}: WhoWasThereProps) {
  const memberEntries = Object.entries(travelParty);
  if (memberEntries.length === 0) return null;
  const presentSet = new Set(selected);

  return (
    <div className="who-here">
      <div className="who-here-head">
        <span className="who-here-label">{label}</span>
        {status === "saving" && <span className="live-notes-status">Saving…</span>}
        {status === "saved" && (
          <span className="live-notes-status live-notes-status-saved">Saved</span>
        )}
        {action}
      </div>
      <div className="who-here-chips">
        {memberEntries.map(([id, member]) => {
          const on = presentSet.has(id);
          return (
            <button
              key={id}
              type="button"
              className={`who-chip${on ? " on" : ""}`}
              aria-pressed={on}
              onClick={() => onToggle(id)}
            >
              <span className="who-chip-av">
                {member.avatarUrl ? (
                  <img src={member.avatarUrl} alt="" />
                ) : (
                  member.displayName.charAt(0).toUpperCase()
                )}
              </span>
              {member.displayName}
            </button>
          );
        })}
      </div>
      {hint && <p className="who-here-hint">{hint}</p>}
    </div>
  );
}

interface WhoWasThereListProps {
  travelParty: Record<string, TravelPartyMember>;
  /** Member keys to show. */
  memberIds: string[];
}

/** Read-only rendering of a resolved group — same chips, nothing to tap. */
export function WhoWasThereList({ travelParty, memberIds }: WhoWasThereListProps) {
  const members = memberIds.map((id) => [id, travelParty[id]] as const).filter(([, m]) => !!m);
  if (members.length === 0) return null;
  return (
    <div className="who-here-chips">
      {members.map(([id, member]) => (
        <span key={id} className="who-chip on who-chip-static">
          <span className="who-chip-av">
            {member!.avatarUrl ? (
              <img src={member!.avatarUrl} alt="" />
            ) : (
              member!.displayName.charAt(0).toUpperCase()
            )}
          </span>
          {member!.displayName}
        </span>
      ))}
    </div>
  );
}
